// src/pages/admin/AdminFinance.jsx
import React, { useState, useEffect } from "react";

export default function AdminFinance() {
  const [filter, setFilter] = useState("This Month");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [financeData, setFinanceData] = useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);

  const [paginatedTransactions, setPaginatedTransactions] = useState([]);
  const [currentTxnPage, setCurrentTxnPage] = useState(1);
  const transactionsPerPage = 5;

  const BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const indexOfLastTxn = currentTxnPage * transactionsPerPage;
  const indexOfFirstTxn = indexOfLastTxn - transactionsPerPage;
  const currentPageTransactions = paginatedTransactions.slice(
    indexOfFirstTxn,
    indexOfLastTxn
  );

  useEffect(() => {
    const fetchFinance = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${BASE_URL}/api/admin/finance?filter=${encodeURIComponent(
            filter
          )}&year=${selectedYear}`,
          {
            method: "GET",
            credentials: "include", // send HttpOnly cookie
          }
        );

        if (!res.ok) throw new Error("Failed to fetch finance data");

        const data = await res.json();
        setFinanceData(data);

        // Save backend transactions into paginated state
        setPaginatedTransactions(data.recentTransactions || []);
        setCurrentTxnPage(1); // reset to first page when filter changes
      } catch (err) {
        console.error(err);
        setFinanceData(null);
        setPaginatedTransactions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFinance();
  }, [filter, selectedYear, BASE_URL]);

  // After fetching transactions
  useEffect(() => {
    if (!financeData?.recentTransactions) return;

    // ✅ Exclude "Pending Payment" orders before paginating
    const validTxns = financeData.recentTransactions.filter(
      (txn) => txn.status.toLowerCase() !== "pending payment"
    );

    setPaginatedTransactions(validTxns);
  }, [financeData]);


  if (loading) return <div className="p-6">Loading finance dashboard...</div>;
  if (!financeData)
    return (
      <div className="text-center p-6 text-red-500">
        Failed to load finance dashboard.
      </div>
    );

  const {
    totalSales = 0,
    ordersThisMonth = 0,
    inventoryValue = 0,
    monthlySales = [],
    lowStockItems = [],
    availableYears = [],
  } = financeData;

  // ✅ Fallback: if backend doesn't return availableYears (e.g. older deploy),
  // build a sensible default list so the dropdown still works.
  const currentYear = new Date().getFullYear();
  const yearOptions =
    availableYears.length > 0
      ? availableYears
      : [currentYear, currentYear - 1, currentYear - 2];

  // ✅ Only show the period filter when viewing the current year.
  // For past years, we always show full-year data (filters like "This Month"
  // don't have a sensible meaning for historical browsing).
  const isCurrentYear = selectedYear === currentYear;

  return (
    <div className="max-w-6xl mx-auto bg-white p-6 rounded shadow">
      {/* Header + Filters */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold">Finance Management</h1>
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
            className="border px-3 py-2 rounded"
          >
            {yearOptions.map((yr) => (
              <option key={yr} value={yr}>
                {yr}
              </option>
            ))}
          </select>
          {isCurrentYear && (
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border px-3 py-2 rounded"
            >
              <option>This Month</option>
              <option>Last Month</option>
              <option>This Quarter</option>
              <option>This Year</option>
            </select>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="bg-green-50 p-4 rounded shadow text-center">
          <h2 className="text-lg font-semibold">
            {isCurrentYear ? "Total Sales" : `Total Sales ${selectedYear}`}
          </h2>
          <p className="text-2xl font-bold text-green-600 mt-2">
            ${totalSales.toLocaleString()}
          </p>
        </div>
        <div className="bg-blue-50 p-4 rounded shadow text-center">
          <h2 className="text-lg font-semibold">
            {isCurrentYear ? "Orders This Period" : `Orders in ${selectedYear}`}
          </h2>
          <p className="text-2xl font-bold text-blue-600 mt-2">
            {ordersThisMonth.toLocaleString()}
          </p>
        </div>
        <div className="bg-purple-50 p-4 rounded shadow text-center">
          <h2 className="text-lg font-semibold">Inventory Value</h2>
          <p className="text-2xl font-bold text-purple-600 mt-2">
            ${inventoryValue.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Monthly Sales Overview */}
      <div className="mb-10">
        <h2 className="text-xl font-semibold mb-4">
          Monthly Sales Overview — {selectedYear}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {monthlySales.map((item) => (
            <div
              key={item.month}
              className="bg-blue-50 p-4 rounded shadow text-center"
            >
              <h3 className="text-md font-semibold">{item.month}</h3>
              <p className="text-lg text-blue-700 font-bold">
                ${item.sales.toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Low Stock Alerts with Pagination */}
      <div className="mb-10">
        <h2 className="text-xl font-semibold mb-4">Low Stock Alerts</h2>

        {lowStockItems.length > 0 ? (
          (() => {
            // ✅ Sort items: Out of stock first, then by ascending stockQty
            const sortedItems = [...lowStockItems].sort((a, b) => {
              const aQty = a.stockQty ?? 0;
              const bQty = b.stockQty ?? 0;
              if (aQty === 0 && bQty !== 0) return -1;
              if (aQty !== 0 && bQty === 0) return 1;
              return aQty - bQty;
            });

            // ✅ Pagination setup
            const itemsPerPage = 10;
            const totalPages = Math.ceil(sortedItems.length / itemsPerPage);

            const startIdx = (currentPage - 1) * itemsPerPage;
            const currentItems = sortedItems.slice(startIdx, startIdx + itemsPerPage);

            const handlePrev = () => {
              if (currentPage > 1) setCurrentPage((prev) => prev - 1);
            };

            const handleNext = () => {
              if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
            };

            return (
              <div>
                <ul className="space-y-2">
                  {currentItems.map((item, idx) => {
                    const qty = item.stockQty ?? 0;
                    const isOutOfStock = qty === 0;
                    return (
                      <li
                        key={idx}
                        className={`px-4 py-2 rounded flex justify-between items-center ${
                          isOutOfStock
                            ? "bg-red-100 text-red-700"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {item.name}
                        <span className="font-bold">
                          {isOutOfStock ? "Out of Stock" : `${qty.toLocaleString()} left`}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                {/* ✅ Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-4 mt-4">
                    <button
                      onClick={handlePrev}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 rounded ${
                        currentPage === 1
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-blue-500 text-white hover:bg-blue-600"
                      }`}
                    >
                      Previous
                    </button>

                    <span className="text-sm text-gray-600">
                      Page {currentPage} of {totalPages}
                    </span>

                    <button
                      onClick={handleNext}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1 rounded ${
                        currentPage === totalPages
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-blue-500 text-white hover:bg-blue-600"
                      }`}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            );
          })()
        ) : (
          <p className="text-gray-500">No low stock items.</p>
        )}
      </div>

      {/* Recent Transactions Table */}
      <div>
        <h2 className="text-xl font-semibold mb-4">
          Recent Transactions — {selectedYear}
        </h2>
        <div className="overflow-x-auto">
          <table className="min-w-full border text-sm">
            <thead>
              <tr className="bg-gray-200 text-left">
                <th className="p-2 border">Order ID</th>
                <th className="p-2 border">Customer</th>
                <th className="p-2 border">Amount</th>
                <th className="p-2 border">Date</th>
                <th className="p-2 border">Status</th>
              </tr>
            </thead>
            <tbody>
              {currentPageTransactions.map((txn) => (
                <tr key={txn.id}>
                  <td className="p-2 border">{txn.id}</td>
                  <td className="p-2 border">
                    {txn.customer} ({txn.customerEmail})
                  </td>
                  <td className="p-2 border">${txn.amount.toFixed(2)}</td>
                  <td className="p-2 border">{txn.date}</td>
                  <td
                    className={`p-2 border font-medium ${
                      // ✅ Status color mapping aligned with Order schema enum:
                      // Green = money earned / completed successfully
                      // Yellow = in progress, needs action
                      // Red = failed / no revenue
                      txn.status === "Delivered" || txn.status === "Paid"
                        ? "text-green-600"
                        : txn.status === "Processing" ||
                          txn.status === "In Transit" ||
                          txn.status === "Pending Payment" ||
                          txn.status === "Pending"
                        ? "text-yellow-600"
                        : "text-red-600"
                    }`}
                  >
                    {txn.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="flex justify-center mt-4 gap-2">
          <button
            onClick={() => setCurrentTxnPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentTxnPage === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Prev
          </button>

          {Array.from(
            { length: Math.ceil(paginatedTransactions.length / transactionsPerPage) },
            (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentTxnPage(i + 1)}
                className={`px-3 py-1 border rounded ${
                  currentTxnPage === i + 1 ? "bg-gray-200 font-bold" : ""
                }`}
              >
                {i + 1}
              </button>
            )
          )}

          <button
            onClick={() =>
              setCurrentTxnPage((prev) =>
                prev < Math.ceil(paginatedTransactions.length / transactionsPerPage)
                  ? prev + 1
                  : prev
              )
            }
            disabled={currentTxnPage === Math.ceil(paginatedTransactions.length / transactionsPerPage)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
